import DOMPurify from 'dompurify';

// Helpers de richtext compartidos por bloques que renderizan HTML sanitizado
// (LegalText, Faq, etc.). Centraliza el pipeline: detección de HTML, escape de
// texto plano, sanitización con DOMPurify y linkify de URLs sueltas.

const HTML_TAG_REGEX = /<\/?[a-z][\s\S]*>/i;
const URL_REGEX = /https?:\/\/[^\s<>"']+/gi;
const BLOCK_TAGS_REGEX = /<\/(p|div|li|h[1-6]|ul|ol|br|blockquote|table|tr|td|th)>/gi;
const ANY_TAG_REGEX = /<[^>]*>/g;
const WHITESPACE_RUN_REGEX = /[ \t\f\v]+/g;
const NEWLINE_RUN_REGEX = /\n{3,}/g;

const escapeHtml = (value) => value
  .replaceAll('&', '&amp;')
  .replaceAll('<', '&lt;')
  .replaceAll('>', '&gt;')
  .replaceAll('"', '&quot;')
  .replaceAll("'", '&#39;');

const plainTextToHtml = (value) => value
  .split(/\n{2,}/)
  .map((block) => `<p>${escapeHtml(block).replace(/\n/g, '<br />')}</p>`)
  .join('');

const splitUrlAndTrailingPunctuation = (value) => {
  let url = value;
  let trailing = '';

  while (url && /[),.;:!?]$/.test(url)) {
    trailing = `${url.slice(-1)}${trailing}`;
    url = url.slice(0, -1);
  }

  return { url, trailing };
};

const linkifyHtml = (html) => {
  if (typeof document === 'undefined') return html;

  const container = document.createElement('div');
  container.innerHTML = html;

  const walker = document.createTreeWalker(
    container,
    NodeFilter.SHOW_TEXT,
    {
      acceptNode: (node) => {
        const parentTag = node.parentElement?.tagName?.toLowerCase();

        if (!node.nodeValue || !URL_REGEX.test(node.nodeValue)) {
          URL_REGEX.lastIndex = 0;
          return NodeFilter.FILTER_REJECT;
        }

        URL_REGEX.lastIndex = 0;

        if (parentTag === 'a' || parentTag === 'script' || parentTag === 'style') {
          return NodeFilter.FILTER_REJECT;
        }

        return NodeFilter.FILTER_ACCEPT;
      },
    }
  );

  const textNodes = [];
  let currentNode = walker.nextNode();

  while (currentNode) {
    textNodes.push(currentNode);
    currentNode = walker.nextNode();
  }

  textNodes.forEach((node) => {
    const text = node.nodeValue || '';
    const fragment = document.createDocumentFragment();
    let lastIndex = 0;
    let match;

    URL_REGEX.lastIndex = 0;

    while ((match = URL_REGEX.exec(text)) !== null) {
      const matchedValue = match[0];
      const start = match.index;

      if (start > lastIndex) {
        fragment.appendChild(document.createTextNode(text.slice(lastIndex, start)));
      }

      const { url, trailing } = splitUrlAndTrailingPunctuation(matchedValue);

      if (url) {
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.textContent = url;
        fragment.appendChild(link);
      }

      if (trailing) {
        fragment.appendChild(document.createTextNode(trailing));
      }

      lastIndex = start + matchedValue.length;
    }

    if (lastIndex < text.length) {
      fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
    }

    node.parentNode?.replaceChild(fragment, node);
  });

  return container.innerHTML;
};

/**
 * Convierte una fuente de texto (HTML o texto plano) en HTML seguro y listo
 * para inyectar con dangerouslySetInnerHTML. Pipeline:
 *   1. Si parece HTML se respeta; si no, se envuelve en <p> y se escapa.
 *   2. Sanitización con DOMPurify (profile html, sin <img>).
 *   3. Linkify de URLs sueltas tras la sanitización.
 *
 * Devuelve string vacío si no hay contenido.
 */
export const sanitizeRichtext = (rawValue) => {
  const rawContent = typeof rawValue === 'string'
    ? rawValue.replace(/\r\n/g, '\n').trim()
    : '';

  if (!rawContent) return '';

  const htmlSource = HTML_TAG_REGEX.test(rawContent)
    ? rawContent
    : plainTextToHtml(rawContent);

  const sanitizedHtml = DOMPurify.sanitize(htmlSource, {
    USE_PROFILES: { html: true },
    FORBID_TAGS: ['img'],
  });

  return linkifyHtml(sanitizedHtml);
};

/**
 * Convierte HTML a texto plano: reemplaza etiquetas de bloque por saltos,
 * elimina el resto de tags y colapsa espacios. Útil para feeds de texto puro
 * (FAQPage JSON-LD, llms.txt) donde no se admite HTML.
 */
export const stripHtml = (rawValue) => {
  const raw = typeof rawValue === 'string' ? rawValue : '';
  if (!raw) return '';

  const withBreaks = raw
    .replace(BLOCK_TAGS_REGEX, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(ANY_TAG_REGEX, '');

  const decoded = withBreaks
    .replaceAll('&nbsp;', ' ')
    .replaceAll('&amp;', '&')
    .replaceAll('&lt;', '<')
    .replaceAll('&gt;', '>')
    .replaceAll('&quot;', '"')
    .replaceAll('&#39;', "'")
    .replaceAll('&apos;', "'");

  return decoded
    .replace(WHITESPACE_RUN_REGEX, ' ')
    .replace(NEWLINE_RUN_REGEX, '\n\n')
    .split('\n')
    .map((line) => line.trimEnd())
    .join('\n')
    .trim();
};
